/**
 * PORTED — RO-3-00g / #44474.
 * Origin: `excellent/app/_modes/strategic-goals-surface.tsx`.
 *
 * Changes:
 *   `useSearchParams` from next/navigation → `useSearch` from @excellent/ui/router
 *   `create/updateObjectiveAction`, `create/updateKeyResultAction`
 *     → `apiCall(objectives.*)` (RO-2-09); `objectives.summary` already via useApi (RO-1-16)
 */

import { useState, useTransition, type ReactNode } from "react";
import { useSearch } from "@excellent/ui/router";
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FormField,
  Icon,
  IntentBadge,
  Input,
  Link as UILink,
  PageHeader,
  SectionTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
  formatDate,
} from "@excellent/ui";
import { Plus } from "@excellent/ui/icons";
import { apiCall, useApi } from "@excellent/ui/api";
import {
  objectives as objectivesContract,
  type ObjectiveDTO,
  type ObjectiveKeyResultDTO,
  type ObjectiveBandDTO,
} from "@excellent/ui/api/contracts/objectives";

/**
 * The Strategic-goals surface — the human-owned half of one Goal concept (v133
 * Objectives→Goals convergence). A strategic goal is a metric with a committed
 * target, measured by key results and banded in the same language as the
 * function-health wall ("are we on plan?" === "is this bar above its benchmark?").
 * Its agentic sibling (fleet-pursued, measured by linked tasks) renders beneath in
 * the merged Plan tab. This surface is READ + WRITE: create a strategic goal, edit
 * it, add key results, and move the numbers. Writes still go through the
 * `objective-actions` server actions (contract/action names stay `objective*` —
 * the underlying rows ARE strategic goals; renaming those is a later phase).
 */

const BAND_INTENT: Record<ObjectiveBandDTO, "success" | "warning" | "danger" | "neutral"> = {
  ok: "success",
  watch: "warning",
  risk: "danger",
  pending: "neutral",
};
const BAND_LABEL: Record<ObjectiveBandDTO, string> = {
  ok: "on track",
  watch: "watch",
  risk: "at risk",
  pending: "pending",
};
const BAND_FILL: Record<ObjectiveBandDTO, string> = {
  ok: "bg-success",
  watch: "bg-warning",
  risk: "bg-danger",
  pending: "bg-muted-foreground/30",
};

/** An agentic goal a strategic goal can drive (from `objectives.summary`). */
type GoalOption = { uid: string; title: string };
/** Radix Select can't carry an empty-string value, so "no linked goal" is a sentinel. */
const NO_GOAL = "__none__";

export function StrategicGoalsSurface() {
  const { data, isLoading, mutate } = useApi(
    objectivesContract.summary,
    { query: {} },
    { keepPreviousData: true },
  );
  const focus = useSearch().get("focus");
  const objs = data?.objectives ?? [];
  const summary = data?.summary;
  const goalOptions = data?.goalOptions ?? [];
  const refresh = () => {
    void mutate();
  };

  return (
    // Full-width mode content (UI-UX-PATTERNS-PRD Issue 9) — no centered narrow
    // column; the Plan surface fills the mode content width like every other page.
    <div className="flex w-full flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Strategic goals"
        subtitle="Strategy on the pulse — every strategic goal is a metric with a committed target. “Are we on plan?” is “is this above its benchmark?”"
        toolbar={
          <ObjectiveFormDialog
            mode="create"
            goalOptions={goalOptions}
            onSaved={refresh}
            trigger={
              <Button
                size="icon-tb"
                aria-label="New strategic goal"
                title="New strategic goal"
              >
                <Plus className="size-4" />
              </Button>
            }
          />
        }
      />
      {summary && summary.total > 0 && <SummaryStrip summary={summary} />}
      {isLoading && !data ? (
        <Card>
          <div className="px-6 py-10 text-center type-body text-muted-foreground">
            Loading strategic goals…
          </div>
        </Card>
      ) : objs.length === 0 ? (
        <EmptyObjectives onSaved={refresh} goalOptions={goalOptions} />
      ) : (
        <div className="flex flex-col gap-4">
          {objs.map((o) => (
            <ObjectiveCard
              key={o.uid}
              o={o}
              focused={o.uid === focus}
              onChanged={refresh}
              goalOptions={goalOptions}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryStrip({
  summary,
}: {
  summary: { total: number; onTrack: number; watch: number; risk: number; pending: number };
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 type-subtext">
      <span className="text-muted-foreground">
        {summary.total} strategic goal{summary.total === 1 ? "" : "s"} · on plan:
      </span>
      <IntentBadge intent="success">{summary.onTrack} on track</IntentBadge>
      {summary.watch > 0 && <IntentBadge intent="warning">{summary.watch} watch</IntentBadge>}
      {summary.risk > 0 && <IntentBadge intent="danger">{summary.risk} at risk</IntentBadge>}
      {summary.pending > 0 && (
        <IntentBadge intent="neutral">{summary.pending} pending</IntentBadge>
      )}
    </div>
  );
}

function ObjectiveCard({
  o,
  focused,
  onChanged,
  goalOptions,
}: {
  o: ObjectiveDTO;
  focused: boolean;
  onChanged: () => void;
  goalOptions: GoalOption[];
}) {
  return (
    <Card
      className={cn(focused && "ring-2 ring-primary/40")}
      // Anchor so a ?focus deep-link from a hero bar can scroll to it.
      id={`obj-${o.uid}`}
    >
      <SectionTitle
        title={o.title}
        subtitle={o.description ?? undefined}
        action={
          <span className="flex items-center gap-2">
            <IntentBadge intent={BAND_INTENT[o.band]} withDot>
              {o.health != null ? `${o.health}/100 · ${BAND_LABEL[o.band]}` : BAND_LABEL[o.band]}
            </IntentBadge>
            <ObjectiveFormDialog
              mode="edit"
              initial={o}
              goalOptions={goalOptions}
              onSaved={onChanged}
              trigger={
                <Button variant="ghost" size="sm">
                  Edit
                </Button>
              }
            />
          </span>
        }
      />
      <div className="flex flex-col gap-3 px-4 pb-4 pt-2">
        <div className="flex flex-wrap items-center gap-2 type-subtext text-muted-foreground">
          {o.projectName && <Badge variant="outline">{o.projectName}</Badge>}
          {o.period && <Badge variant="neutral">{o.period}</Badge>}
          {o.dueOn && <span>due {formatDate(o.dueOn)}</span>}
          {o.goalUid && (
            <UILink
              href={`/o/${o.goalUid}`}
              variant="muted"
              className="inline-flex items-center gap-1"
            >
              <Icon name="link" size="xs" />
              agentic goal
            </UILink>
          )}
        </div>
        {o.keyResults.length === 0 ? (
          <p className="type-subtext text-muted-foreground">
            No key results yet — add one to put this strategic goal on the pulse.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border/40">
            {o.keyResults.map((kr) => (
              <KeyResultRow key={kr.uid} kr={kr} band={o.band} onChanged={onChanged} />
            ))}
          </ul>
        )}
        <div>
          <KeyResultFormDialog
            mode="create"
            objectiveUid={o.uid}
            onSaved={onChanged}
            trigger={
              <Button
                variant="outline"
                size="icon-tb"
                aria-label="Add key result"
                title="Add key result"
              >
                <Plus className="size-4" />
              </Button>
            }
          />
        </div>
      </div>
    </Card>
  );
}

function KeyResultRow({
  kr,
  band,
  onChanged,
}: {
  kr: ObjectiveKeyResultDTO;
  band: ObjectiveBandDTO;
  onChanged: () => void;
}) {
  const pct = kr.attainment == null ? null : Math.round(kr.attainment * 100);
  const unit = kr.unit ? ` ${kr.unit}` : "";
  const value =
    kr.currentValue == null
      ? "—"
      : `${kr.currentValue}${unit}${kr.targetValue != null ? ` / ${kr.targetValue}${unit}` : ""}`;
  return (
    <li className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
      <span className="min-w-0 flex-1 truncate type-body">{kr.title}</span>
      <span className="tabular-nums type-subtext text-muted-foreground">{value}</span>
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
        {pct != null && (
          <span
            className={cn("block h-full rounded-full", BAND_FILL[band])}
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        )}
      </div>
      <span className="w-10 text-right tabular-nums type-subtext text-muted-foreground">
        {pct == null ? "—" : `${pct}%`}
      </span>
      <KeyResultFormDialog
        mode="edit"
        initial={kr}
        onSaved={onChanged}
        trigger={
          <Button variant="ghost" size="sm">
            Edit
          </Button>
        }
      />
    </li>
  );
}

function EmptyObjectives({
  onSaved,
  goalOptions,
}: {
  onSaved: () => void;
  goalOptions: GoalOption[];
}) {
  return (
    <Card>
      <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
        <Icon name="target" size="base" className="text-muted-foreground" />
        <div>
          <p className="type-body font-medium">No strategic goals tracked yet</p>
          <p className="mx-auto mt-1 max-w-md type-subtext text-muted-foreground">
            A strategic goal is a metric with a committed target. Set the 1–3 outcomes you’re
            steering toward this period — each shows up banded alongside your function health.
          </p>
        </div>
        <ObjectiveFormDialog
          mode="create"
          goalOptions={goalOptions}
          onSaved={onSaved}
          trigger={
            <Button
              size="icon-tb"
              aria-label="Set a strategic goal"
              title="Set a strategic goal"
            >
              <Plus className="size-4" />
            </Button>
          }
        />
      </div>
    </Card>
  );
}

// ── Create / edit dialogs ─────────────────────────────────────────────────────

function ObjectiveFormDialog({
  mode,
  initial,
  goalOptions,
  onSaved,
  trigger,
}: {
  mode: "create" | "edit";
  initial?: ObjectiveDTO;
  goalOptions: GoalOption[];
  onSaved: () => void;
  trigger: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pending, start] = useTransition();
  const [err, setErr] = useState<string | null>(null);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [period, setPeriod] = useState(initial?.period ?? "");
  const [dueOn, setDueOn] = useState((initial?.dueOn ?? "").slice(0, 10));
  const [goalUid, setGoalUid] = useState(initial?.goalUid ?? "");

  function submit() {
    setErr(null);
    start(async () => {
      const res =
        mode === "create"
          ? await apiCall(objectivesContract.create, { body: { title, description, period, dueOn, goalUid } })
          : await apiCall(objectivesContract.update, {
              params: { uid: initial!.uid },
              body: { title, description, period, dueOn, goalUid },
            });
      if (res.ok) {
        setOpen(false);
        onSaved();
      } else {
        setErr(res.reason ?? "Couldn’t save.");
      }
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "New strategic goal" : "Edit strategic goal"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <FormField
            label="Strategic goal"
            htmlFor="o-title"
            help="One sentence — the outcome you’re steering toward."
          >
            <Input
              id="o-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Reach $10k MRR"
            />
          </FormField>
          <FormField label="Why it matters" htmlFor="o-desc">
            <Textarea
              id="o-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="The stakes — what this unlocks, or the problem it solves."
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Period" htmlFor="o-period" help="e.g. 2026-Q3">
              <Input
                id="o-period"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                placeholder="2026-Q3"
              />
            </FormField>
            <FormField label="Due" htmlFor="o-due">
              <Input
                id="o-due"
                type="date"
                value={dueOn}
                onChange={(e) => setDueOn(e.target.value)}
              />
            </FormField>
          </div>
          <FormField
            label="Agentic goal"
            htmlFor="o-goal"
            help="The executable goal this strategic goal drives — it links the goal, never forks it."
          >
            <Select
              value={goalUid || NO_GOAL}
              onValueChange={(v) => setGoalUid(v === NO_GOAL ? "" : v)}
            >
              <SelectTrigger id="o-goal">
                <SelectValue placeholder="No linked goal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_GOAL}>No linked goal</SelectItem>
                {goalOptions.map((g) => (
                  <SelectItem key={g.uid} value={g.uid}>
                    {g.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          {err && <p className="type-subtext text-danger">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !title.trim()}>
            {pending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KeyResultFormDialog({
  mode,
  objectiveUid,
  initial,
  onSaved,
  trigger,
}: {
  mode: "create" | "edit";
  objectiveUid?: string;
  initial?: ObjectiveKeyResultDTO;
  onSaved: () => void;
  trigger: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pending, start] = useTransition();
  const [err, setErr] = useState<string | null>(null);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [unit, setUnit] = useState(initial?.unit ?? "");
  const [direction, setDirection] = useState(initial?.direction ?? "increase");
  const [startValue, setStartValue] = useState(initial?.startValue?.toString() ?? "");
  const [targetValue, setTargetValue] = useState(initial?.targetValue?.toString() ?? "");
  const [currentValue, setCurrentValue] = useState(initial?.currentValue?.toString() ?? "");

  function submit() {
    setErr(null);
    start(async () => {
      const res =
        mode === "create"
          ? await apiCall(objectivesContract.createKeyResult, {
              body: {
                objectiveUid: objectiveUid!,
                title,
                unit,
                direction,
                startValue,
                targetValue,
                currentValue,
              },
            })
          : await apiCall(objectivesContract.updateKeyResult, {
              params: { uid: initial!.uid },
              body: {
                title,
                unit,
                direction,
                startValue,
                targetValue,
                currentValue,
              },
            });
      if (res.ok) {
        setOpen(false);
        onSaved();
      } else {
        setErr(res.reason ?? "Couldn’t save.");
      }
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Add key result" : "Edit key result"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <FormField
            label="Key result"
            htmlFor="kr-title"
            help="The metric that proves the strategic goal — e.g. “Monthly recurring revenue”."
          >
            <Input
              id="kr-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Monthly recurring revenue"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Unit" htmlFor="kr-unit">
              <Input
                id="kr-unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="$ / % / count"
              />
            </FormField>
            <FormField label="Direction" htmlFor="kr-dir">
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger id="kr-dir">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="increase">Increase (higher is better)</SelectItem>
                  <SelectItem value="decrease">Decrease (lower is better)</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Start" htmlFor="kr-start">
              <Input
                id="kr-start"
                type="number"
                value={startValue}
                onChange={(e) => setStartValue(e.target.value)}
                placeholder="0"
              />
            </FormField>
            <FormField label="Current" htmlFor="kr-current">
              <Input
                id="kr-current"
                type="number"
                value={currentValue}
                onChange={(e) => setCurrentValue(e.target.value)}
                placeholder="6400"
              />
            </FormField>
            <FormField label="Target" htmlFor="kr-target">
              <Input
                id="kr-target"
                type="number"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder="10000"
              />
            </FormField>
          </div>
          <p className="type-subtext text-muted-foreground">
            Attainment is how far Current has moved from Start toward Target. Leave Target or
            Current blank and the key result reads “pending” — tracked, not failing.
          </p>
          {err && <p className="type-subtext text-danger">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !title.trim()}>
            {pending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Reusable entry points for the consolidated goals home + goal detail page ────
// The strategic list itself is now the one-line GoalsLedger (rows), not these
// cards — so these two exports carry strategic CREATE (home toolbar) and KEY-
// RESULT viewing/editing (goal detail page) over the same dialogs + actions.

/** Toolbar action: create a strategic goal. Fetches the agentic-goal options so
 *  the new strategic goal can link the goal that executes it. */
export function NewStrategicGoalButton() {
  const { data, mutate } = useApi(
    objectivesContract.summary,
    { query: {} },
    { keepPreviousData: true },
  );
  const goalOptions = data?.goalOptions ?? [];
  return (
    <ObjectiveFormDialog
      mode="create"
      goalOptions={goalOptions}
      onSaved={() => void mutate()}
      trigger={
        <Button
          variant="outline"
          size="icon-tb"
          aria-label="New strategic goal"
          title="New strategic goal"
        >
          <Plus className="size-4" />
        </Button>
      }
    />
  );
}

/** The goal detail page's KEY RESULTS panel for a strategic goal — the band, the
 *  key-result list (each editable), add-KR, and edit-goal. Self-fetching by uid so
 *  the server detail page just drops it in. Key results now live HERE, not on a
 *  separate objectives surface. */
export function StrategicKeyResults({ goalUid }: { goalUid: string }) {
  const { data, isLoading, mutate } = useApi(
    objectivesContract.summary,
    { query: {} },
    { keepPreviousData: true },
  );
  const o = data?.objectives.find((x) => x.uid === goalUid);
  const goalOptions = data?.goalOptions ?? [];
  const refresh = () => {
    void mutate();
  };

  if (isLoading && !data) {
    return <p className="type-body text-muted-foreground">Loading key results…</p>;
  }
  if (!o) {
    return (
      <p className="type-body text-muted-foreground">
        No key results yet — add one to put this goal on the pulse.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <IntentBadge intent={BAND_INTENT[o.band]} withDot>
          {o.health != null ? `${o.health}/100 · ${BAND_LABEL[o.band]}` : BAND_LABEL[o.band]}
        </IntentBadge>
        <ObjectiveFormDialog
          mode="edit"
          initial={o}
          goalOptions={goalOptions}
          onSaved={refresh}
          trigger={
            <Button variant="ghost" size="sm">
              Edit goal
            </Button>
          }
        />
      </div>
      {o.keyResults.length === 0 ? (
        <p className="type-body text-muted-foreground">
          No key results yet — add one to put this goal on the pulse.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border/40">
          {o.keyResults.map((kr) => (
            <KeyResultRow key={kr.uid} kr={kr} band={o.band} onChanged={refresh} />
          ))}
        </ul>
      )}
      <div>
        <KeyResultFormDialog
          mode="create"
          objectiveUid={o.uid}
          onSaved={refresh}
          trigger={
            <Button
              variant="outline"
              size="icon-tb"
              aria-label="Add key result"
              title="Add key result"
            >
              <Plus className="size-4" />
            </Button>
          }
        />
      </div>
    </div>
  );
}
